<template>
  <div>
    <!-- Header -->
    <Header title="Statistiques des Shifts">
      <template #icon>
        <span class="material-icons">bar_chart</span>
      </template>
      <template #actions>
        <router-link to="/shifts" class="back-button">
          <span class="material-icons">arrow_back</span>
          Retour à la liste
        </router-link>
      </template>
    </Header>
    
    <div class="shift-stats-container">

    <div class="content-container">
      <!-- Filtres -->
      <div class="filters-card">
        <div class="card-header">
          <h2>Filtres</h2>
        </div>
        <div class="card-body">
          <div class="filters-grid">
            <div class="filter-group">
              <label>Période</label>
              <div class="date-range">
                <input type="date" v-model="filters.debut" @change="loadAllStats" />
                <span>à</span>
                <input type="date" v-model="filters.fin" @change="loadAllStats" />
              </div>
            </div>
            
            <div class="filter-group">
              <label>Shift</label>
              <select v-model="filters.shiftId" @change="loadAllStats">
                <option value="">Tous les shifts</option>
                <option v-for="shift in shifts" :key="shift.id" :value="shift.id">
                  {{ shift.code }} - {{ shift.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Résumé des statistiques -->
      <div class="stats-card">
        <div class="card-header">
          <h2>Résumé des statistiques</h2>
          <div class="header-actions">
            <button class="btn-export" @click="exportStats" title="Exporter les statistiques">
              <span class="material-icons">file_download</span>
              Exporter
            </button>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des statistiques...</p>
          </div>
          
          <div v-else-if="!stats.resume" class="no-data-message">
            <span class="material-icons">bar_chart</span>
            <p>Aucune statistique disponible pour la période sélectionnée</p>
          </div>
          
          <div v-else class="stats-summary-container">
            <div class="stats-summary">
              <div class="stat-item">
                <div class="stat-value">{{ stats.resume.total || 0 }}</div>
                <div class="stat-label">Total pointages</div>
              </div>
              
              <div class="stat-item">
                <div class="stat-value">{{ stats.resume.a_l_heure || 0 }}</div>
                <div class="stat-label">À l'heure</div>
              </div>
              
              <div class="stat-item">
                <div class="stat-value">{{ stats.resume.en_retard || 0 }}</div>
                <div class="stat-label">En retard</div>
              </div>
              
              <div class="stat-item">
                <div class="stat-value">{{ calculatePercentage(stats.resume.a_l_heure, stats.resume.total) }}%</div>
                <div class="stat-label">Ponctualité</div>
              </div>
            </div>
            
            <div class="stats-chart">
              <DoughnutChart 
                :chartData="statusDistributionData"
                :height="200"
              />
            </div>
          </div>
        </div>
      </div>
      
      <!-- Graphique de tendance -->
      <div class="stats-card">
        <div class="card-header">
          <h2>Tendance des pointages</h2>
          <div class="header-actions">
            <button class="btn-export" @click="exportTrendData" title="Exporter les données de tendance">
              <span class="material-icons">file_download</span>
            </button>
            <select v-model="chartType">
              <option value="daily">Par jour</option>
              <option value="weekly">Par semaine</option>
              <option value="monthly">Par mois</option>
            </select>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement du graphique...</p>
          </div>
          
          <div v-else-if="!stats.par_jour || !stats.par_jour.length" class="no-data-message">
            <span class="material-icons">timeline</span>
            <p>Pas assez de données pour afficher un graphique</p>
          </div>
          
          <div v-else class="chart-container">
            <LineChart 
              :chartData="trendChartData" 
              :chartOptions="trendChartOptions"
              :height="350"
            />
          </div>
        </div>
      </div>
      
      <!-- Notifications et alertes -->
      <div class="stats-card" v-if="!filters.shiftId">
        <div class="card-header">
          <h2>Notifications et alertes</h2>
        </div>
        <div class="card-content">
          <ShiftNotifications />
        </div>
      </div>
      
      <!-- Comparaison des shifts -->
      <div class="stats-card" v-if="!filters.shiftId">
        <div class="card-header">
          <h2>Comparaison des shifts</h2>
          <div class="header-actions">
            <button class="btn-export" @click="exportShiftsComparison" title="Exporter la comparaison des shifts">
              <span class="material-icons">download</span>
              Exporter
            </button>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des comparaisons...</p>
          </div>
          
          <div v-else-if="!shiftsComparison.length" class="no-data-message">
            <span class="material-icons">compare</span>
            <p>Aucune donnée de comparaison disponible</p>
          </div>
          
          <div v-else class="comparison-container">
            <div class="chart-container">
              <BarChart 
                :chartData="shiftsComparisonChartData"
                :chartOptions="shiftsComparisonChartOptions"
                :height="300"
              />
            </div>
            
            <div class="table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Shift</th>
                    <th>Total</th>
                    <th>À l'heure</th>
                    <th>En retard</th>
                    <th>% à l'heure</th>
                    <th>Retard moyen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in shiftsComparison" :key="item.shift.id">
                    <td>
                      <div class="shift-info">
                        <div class="shift-color" :style="{ backgroundColor: item.shift.color }"></div>
                        <span>{{ item.shift.name }}</span>
                      </div>
                    </td>
                    <td>{{ item.stats.total }}</td>
                    <td>{{ item.stats.a_l_heure }}</td>
                    <td>{{ item.stats.en_retard }}</td>
                    <td>{{ calculatePercentage(item.stats.a_l_heure, item.stats.total) }}%</td>
                    <td>{{ item.stats.retard_moyen_minutes || 0 }} min</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Top personnel -->
      <div class="stats-card">
        <div class="card-header">
          <h2>Top du personnel</h2>
          <div class="header-actions">
            <select v-model="personnelSortBy">
              <option value="a_l_heure">Plus ponctuels</option>
              <option value="en_retard">Plus en retard</option>
            </select>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des données du personnel...</p>
          </div>
          
          <div v-else-if="!stats.par_personnel || !stats.par_personnel.length" class="no-data-message">
            <span class="material-icons">people</span>
            <p>Aucune donnée de personnel disponible</p>
          </div>
          
          <div v-else class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Personnel</th>
                  <th>Total</th>
                  <th>À l'heure</th>
                  <th>En retard</th>
                  <th>% à l'heure</th>
                  <th>Retard moyen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="personnel in sortedPersonnel" :key="personnel.num_personnel">
                  <td>
                    {{ personnel.personnel?.nom }} {{ personnel.personnel?.prenom }}
                  </td>
                  <td>{{ personnel.total }}</td>
                  <td>{{ personnel.a_l_heure }}</td>
                  <td>{{ personnel.en_retard }}</td>
                  <td>
                    <div class="percentage-bar">
                      <span 
                        class="percentage-fill" 
                        :style="{ width: calculatePercentage(personnel.a_l_heure, personnel.total) + '%' }"
                      ></span>
                      <span>{{ calculatePercentage(personnel.a_l_heure, personnel.total) }}%</span>
                    </div>
                  </td>
                  <td>{{ personnel.retard_moyen_minutes }} min</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import api from '@/services/api';
import Header from '@/components/Header.vue';
import LineChart from '@/components/charts/LineChart.vue';
import BarChart from '@/components/charts/BarChart.vue';
import DoughnutChart from '@/components/charts/DoughnutChart.vue';
import ShiftNotifications from '@/components/notifications/ShiftNotifications.vue';

// États
const loading = ref(true);
const shifts = ref([]);
const stats = ref({});
const shiftsComparison = ref([]);

// Filtres
const filters = reactive({
  debut: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
  fin: new Date().toISOString().split('T')[0],
  shiftId: '',
  groupBy: 'daily'
});

// Données pour les graphiques
const trendChartData = computed(() => {
  if (!stats.value.par_jour || !stats.value.par_jour.length) {
    return { labels: [], datasets: [] };
  }
  
  // Extraction des données par jour
  const labels = stats.value.par_jour.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  });
  
  return {
    labels,
    datasets: [
      {
        label: 'À l\'heure',
        backgroundColor: 'rgba(48, 199, 181, 0.2)',
        borderColor: '#30C7B5', // Turquoise principal
        borderWidth: 2,
        pointBackgroundColor: '#30C7B5',
        pointBorderColor: '#fff',
        pointRadius: 4,
        data: stats.value.par_jour.map(item => item.a_l_heure || 0)
      },
      {
        label: 'En retard',
        backgroundColor: 'rgba(255, 122, 104, 0.2)',
        borderColor: '#FF7A68', // Corail doux
        borderWidth: 2,
        pointBackgroundColor: '#FF7A68',
        pointBorderColor: '#fff',
        pointRadius: 4,
        data: stats.value.par_jour.map(item => item.en_retard || 0)
      }
    ]
  };
});

const trendChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: 'Évolution des pointages',
      font: {
        size: 16,
        family: "'Poppins', sans-serif"
      },
      padding: {
        top: 10,
        bottom: 20
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Nombre de pointages',
        font: {
          family: "'Poppins', sans-serif"
        }
      }
    },
    x: {
      title: {
        display: true,
        text: 'Date',
        font: {
          family: "'Poppins', sans-serif"
        }
      }
    }
  }
};

const statusDistributionData = computed(() => {
  if (!stats.value.resume) {
    return { labels: [], datasets: [] };
  }
  
  return {
    labels: ['À l\'heure', 'En retard', 'Hors plage'],
    datasets: [{
      data: [
        stats.value.resume.a_l_heure || 0,
        stats.value.resume.en_retard || 0,
        stats.value.resume.hors_plage || 0
      ],
      backgroundColor: [
        '#30C7B5', // Turquoise principal
        '#FF7A68', // Corail doux
        '#2B8FA6'  // Bleu marine
      ],
      borderWidth: 2
    }]
  };
});

const shiftsComparisonChartData = computed(() => {
  if (!shiftsComparison.value || !shiftsComparison.value.length) {
    return { labels: [], datasets: [] };
  }
  
  const labels = shiftsComparison.value.map(item => item.shift.name);
  const colors = shiftsComparison.value.map(item => item.shift.color);
  
  return {
    labels,
    datasets: [
      {
        label: 'À l\'heure',
        backgroundColor: colors.map(color => `${color}99`), // Ajout de transparence
        data: shiftsComparison.value.map(item => item.stats.a_l_heure || 0)
      },
      {
        label: 'En retard',
        backgroundColor: '#FF7A68',
        data: shiftsComparison.value.map(item => item.stats.en_retard || 0)
      }
    ]
  };
});

const shiftsComparisonChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: 'Comparaison des shifts',
      font: {
        size: 16,
        family: "'Poppins', sans-serif"
      },
      padding: {
        top: 10,
        bottom: 20
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Nombre de pointages',
        font: {
          family: "'Poppins', sans-serif"
        }
      }
    }
  }
};

// Options d'affichage
const chartType = ref('daily');
const personnelSortBy = ref('a_l_heure');

// Chargement initial
onMounted(async () => {
  await loadShifts();
  await loadAllStats();
});

// Fonctions pour charger les données
async function loadShifts() {
  try {
    const response = await api.get('/api/parametres/shifts');
    shifts.value = response.data.data;
  } catch (error) {
    console.error('Erreur lors du chargement des shifts:', error);
    shifts.value = [];
  }
}

async function loadAllStats() {
  loading.value = true;
  
  try {
    if (filters.shiftId) {
      await loadShiftStats(filters.shiftId);
    } else {
      await Promise.all([
        loadGlobalStats(),
        loadShiftsComparison()
      ]);
    }
  } catch (error) {
    console.error('Erreur lors du chargement des statistiques:', error);
  } finally {
    loading.value = false;
  }
}

async function loadShiftStats(shiftId) {
  try {
    const response = await api.get(`/api/pointage/shift/${shiftId}/stats`, {
      params: {
        debut: filters.debut,
        fin: filters.fin
      }
    });
    stats.value = response.data;
  } catch (error) {
    console.error(`Erreur lors du chargement des statistiques pour le shift ${shiftId}:`, error);
    stats.value = {};
  }
}

async function loadGlobalStats() {
  try {
    const response = await api.get('/api/pointage/stats', {
      params: {
        debut: filters.debut,
        fin: filters.fin
      }
    });
    stats.value = response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des statistiques globales:', error);
    stats.value = {};
  }
}

async function loadShiftsComparison() {
  try {
    const comparison = [];
    
    // Pour chaque shift, charger ses stats
    for (const shift of shifts.value) {
      try {
        const response = await api.get(`/api/pointage/shift/${shift.id}/stats`, {
          params: {
            debut: filters.debut,
            fin: filters.fin
          }
        });
        
        comparison.push({
          id: shift.id,
          code: shift.code,
          name: shift.name,
          color: shift.color,
          stats: response.data.data.resume
        });
      } catch (error) {
        console.error(`Erreur lors du chargement des statistiques pour le shift ${shift.id}:`, error);
      }
    }
    
    shiftsComparison.value = comparison;
  } catch (error) {
    console.error('Erreur lors du chargement de la comparaison des shifts:', error);
    shiftsComparison.value = [];
  }
}

// Computed properties
const sortedPersonnel = computed(() => {
  if (!stats.value.par_personnel) return [];
  
  return [...stats.value.par_personnel].sort((a, b) => {
    if (personnelSortBy.value === 'a_l_heure') {
      // Trier par pourcentage à l'heure (décroissant)
      const percentA = calculatePercentage(a.a_l_heure, a.total);
      const percentB = calculatePercentage(b.a_l_heure, b.total);
      return percentB - percentA;
    } else {
      // Trier par pourcentage en retard (décroissant)
      const percentA = calculatePercentage(a.en_retard, a.total);
      const percentB = calculatePercentage(b.en_retard, b.total);
      return percentB - percentA;
    }
  }).slice(0, 10); // Limiter à 10 résultats
});

// Fonctions utilitaires
function calculatePercentage(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

// Fonctions d'export
function exportStats() {
  if (!stats.value.resume) return;
  
  const data = {
    periode: `${filters.debut} au ${filters.fin}`,
    total_pointages: stats.value.resume.total || 0,
    a_l_heure: stats.value.resume.a_l_heure || 0,
    en_retard: stats.value.resume.en_retard || 0,
    pourcentage_ponctualite: calculatePercentage(stats.value.resume.a_l_heure, stats.value.resume.total),
    retard_moyen: `${stats.value.resume.retard_moyen_minutes || 0} min`
  };
  
  downloadCSV([data], 'resume_statistiques_shifts');
}

function exportTrendData() {
  if (!stats.value.par_jour || !stats.value.par_jour.length) return;
  
  const data = stats.value.par_jour.map(item => ({
    date: item.date,
    total: item.total || 0,
    a_l_heure: item.a_l_heure || 0,
    en_retard: item.en_retard || 0,
    pourcentage_ponctualite: calculatePercentage(item.a_l_heure, item.total)
  }));
  
  downloadCSV(data, 'tendance_pointages_shifts');
}

function exportShiftsComparison() {
  if (!shiftsComparison.value || !shiftsComparison.value.length) return;
  
  const data = shiftsComparison.value.map(item => ({
    shift_code: item.shift.code,
    shift_name: item.shift.name,
    total: item.stats.total || 0,
    a_l_heure: item.stats.a_l_heure || 0,
    en_retard: item.stats.en_retard || 0,
    pourcentage_ponctualite: calculatePercentage(item.stats.a_l_heure, item.stats.total),
    retard_moyen: `${item.stats.retard_moyen_minutes || 0} min`
  }));
  
  downloadCSV(data, 'comparaison_shifts');
}

function downloadCSV(data, filename) {
  if (!data || !data.length) return;
  
  // Créer les en-têtes
  const headers = Object.keys(data[0]);
  
  // Créer les lignes
  const csvRows = [];
  csvRows.push(headers.join(','));
  
  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header];
      // Échapper les virgules et les guillemets
      return `"${value}"`;
    });
    csvRows.push(values.join(','));
  }
  
  // Combiner en une seule chaîne CSV
  const csvContent = csvRows.join('\n');
  
  // Créer un blob et télécharger
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.shift-stats-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 64px);
}

.content-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 20px;
  padding: 0 20px 20px;
}

/* Styles pour l'en-tête */
:deep(.header) {
  position: sticky;
  top: 0;
  z-index: 100;
}

/* Styles pour le bouton de retour dans le Header */
:deep(.back-button) {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

:deep(.back-button:hover) {
  color: var(--turquoise-deep);
  transform: translateX(-3px);
}

:deep(.back-button .material-icons) {
  margin-right: 5px;
}



/* Styles pour les boutons d'export */
.btn-export {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: var(--turquoise-primary, #30C7B5);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-export:hover {
  background-color: var(--turquoise-dark, #26A194);
}

.btn-export .material-icons {
  font-size: 18px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
}

.filters-card, .stats-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.card-body {
  padding: 20px;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-size: 14px;
  color: #6b7280;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-range input, .filter-group select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: white;
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  position: relative;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: var(--turquoise-primary);
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
}

.stat-percentage {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
  font-size: 12px;
  font-weight: bold;
  padding: 3px 8px;
  border-radius: 12px;
}

.chart-container {
  height: 300px;
  margin-top: 10px;
}

.chart-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #d1d5db;
}

.chart-note {
  font-size: 12px;
  color: #6b7280;
  margin-top: 10px;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th, .data-table td {
  padding: 12px 15px;
  text-align: left;
}

.data-table th {
  background-color: #f9fafb;
  font-weight: 500;
  color: #374151;
}

.data-table tr {
  border-bottom: 1px solid #e5e7eb;
}

.data-table tr:last-child {
  border-bottom: none;
}

.shift-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.shift-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.shift-code {
  font-weight: 500;
  color: #111827;
}

.shift-name {
  font-size: 12px;
  color: #6b7280;
}

.percentage-bar {
  width: 100%;
  height: 20px;
  background-color: #f3f4f6;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
}

.percentage-fill {
  height: 100%;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  border-radius: 10px;
}

.percentage-bar span {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111827;
  font-size: 12px;
  font-weight: 500;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--turquoise-primary);
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #6b7280;
}

.no-data-message .material-icons {
  font-size: 48px;
  margin-bottom: 15px;
  color: var(--turquoise-very-light);
}

.action-btn {
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #374151;
}
</style>
